// Global singleton audio manager for the Home ambient track
// ("The Green Awakening").
//
// The <audio> element is created ONCE, attached to document.body outside
// the React tree, and reused for the entire browser tab lifetime. This
// lets the track keep playing seamlessly when the user navigates between:
//   • the Home swipe screens (HeroVideo <-> ValuesScreen)
//   • Home -> /chi-siamo -> back
//   • Home -> /missione -> back
// …without ever creating a second audio instance or restarting from 0.
//
// The audio is only stopped explicitly via `fadeOutAndStop()` which is
// called from the "Entra in ToClean" CTA in HeroVideo.

let audioEl = null;
let currentSrc = null;
let currentMuted = false;
let startedByGesture = false;
let fadeRaf = null;
const mutedSubs = new Set();

function ensureAudio(src) {
  if (audioEl) return audioEl;
  const a = document.createElement("audio");
  a.src = src;
  a.loop = true;
  a.preload = "auto";
  a.setAttribute("playsinline", "");
  // playsInline as a DOM property does not exist on plain <audio>; the
  // attribute is the safe cross-browser form.
  a.autoplay = true;
  a.muted = false;
  a.volume = 0; // fade-in from silence when playback actually starts
  a.setAttribute("data-testid", "home-audio-global");
  a.style.display = "none";
  document.body.appendChild(a);
  audioEl = a;
  currentSrc = src;
  return audioEl;
}

function cancelFade() {
  if (fadeRaf) {
    cancelAnimationFrame(fadeRaf);
    fadeRaf = null;
  }
}

function fadeVolumeTo(target, durationMs) {
  const a = audioEl;
  if (!a) return;
  cancelFade();
  const from = a.volume;
  const to = Math.max(0, Math.min(1, target));
  const startedAt = performance.now();
  const step = () => {
    const p = Math.min(1, (performance.now() - startedAt) / Math.max(1, durationMs));
    a.volume = Math.max(0, Math.min(1, from + (to - from) * p));
    if (p < 1) fadeRaf = requestAnimationFrame(step);
    else fadeRaf = null;
  };
  fadeRaf = requestAnimationFrame(step);
}

export const homeAudio = {
  /** Initialise (idempotent). Returns the underlying element. */
  init(src) {
    ensureAudio(src);
    if (currentSrc !== src && audioEl) {
      // Different track — only replace if empty (defensive; single-track today)
      if (!audioEl.currentTime) {
        audioEl.src = src;
        currentSrc = src;
      }
    }
    return audioEl;
  },
  /** Try to start playback (respects autoplay policies). */
  play() {
    const a = audioEl;
    if (!a) return Promise.resolve();
    const p = a.play();
    return (p && p.then ? p : Promise.resolve()).catch(() => {});
  },
  /** Ensure playback is active AND ramp up to `targetVolume` if it isn't yet. */
  ensurePlayingWithFade(targetVolume, fadeMs = 800) {
    const a = audioEl;
    if (!a) return Promise.resolve();
    const wasBelow = a.volume < targetVolume - 0.02;
    return this.play().then(() => {
      if (wasBelow) fadeVolumeTo(targetVolume, fadeMs);
    });
  },
  pause() {
    if (!audioEl) return;
    try { audioEl.pause(); } catch { /* noop */ }
  },
  isPaused() {
    return !audioEl || audioEl.paused;
  },
  hasStartedByGesture() { return startedByGesture; },
  markStartedByGesture() { startedByGesture = true; },
  setMuted(m) {
    currentMuted = !!m;
    if (audioEl) audioEl.muted = currentMuted;
    mutedSubs.forEach((cb) => { try { cb(currentMuted); } catch { /* noop */ } });
  },
  getMuted() { return currentMuted; },
  subscribeMuted(cb) {
    mutedSubs.add(cb);
    return () => mutedSubs.delete(cb);
  },
  /** Fade out to 0 volume over `ms`, then pause. Resolves on completion. */
  fadeOutAndStop(ms = 420) {
    return new Promise((resolve) => {
      const a = audioEl;
      cancelFade();
      if (!a || a.paused) { resolve(); return; }
      const start = a.volume;
      const startedAt = performance.now();
      const step = () => {
        const p = Math.min(1, (performance.now() - startedAt) / ms);
        a.volume = Math.max(0, start * (1 - p));
        if (p < 1) fadeRaf = requestAnimationFrame(step);
        else {
          fadeRaf = null;
          try { a.pause(); } catch { /* noop */ }
          resolve();
        }
      };
      fadeRaf = requestAnimationFrame(step);
    });
  },
};
